import { Box, Chip, Tooltip, TooltipProps, styled, tooltipClasses, useTheme } from '@mui/material';
import { Typography } from '../../../components/Typography/Typography';
import {
  MonthlyTimesheet,
  TimesheetLog,
  TimesheetLogType,
  TimesheetStatus,
  User,
  UserRole,
  WorkflowStage,
} from '../../../types';
import { useCallback, useMemo } from 'react';
import { calculateDaysBetweenDates } from '../../../utils/formatters';
import { format, parseISO } from 'date-fns';

interface Props {
  children: JSX.Element;
  timesheet: MonthlyTimesheet;
  operatingPartner: User | undefined;
}

export const PendingStatusTooltip = ({ children, timesheet, operatingPartner }: Props) => {
  const { colors } = useTheme();

  const isPendingDealOwners = timesheet.status === TimesheetStatus.PENDING_DEAL_OWNERS;
  // Asset Services users never go through Deal Owner approval, so hide the Deal Owner logs.
  const isAssetServices = Boolean(operatingPartner?.isAssetServices);

  const logsByRole = useMemo(() => {
    const allLogs = timesheet.timesheetLogs
      ?.sort((a, b) => a.id - b.id)
      .filter(
        (log) => log.cycle === Math.max(...(timesheet.timesheetLogs ?? []).map((i) => i.cycle))
      );
    return {
      op: allLogs?.find((log) => log.role === UserRole.OPERATING_PARTNER),
      accountant: allLogs?.find((log) => log.role === UserRole.ACCOUNTANT),
      opManagers: allLogs?.filter((log) => log.role === UserRole.OP_MANAGER),
      // SVP Manager approval logs carry a null role and are identified by their stage.
      svpManagers: allLogs?.filter((log) => log.stage === WorkflowStage.SVP_MANAGER),
      dealOwners: allLogs?.filter((log) => log.role === UserRole.DEAL_OWNER),
    };
  }, [timesheet.timesheetLogs]);

  if (!operatingPartner) return <></>;

  return (
    <StyledTooltip
      enterDelay={300}
      enterNextDelay={300}
      title={
        <Box paddingY={1}>
          <TitleWrapper>
            <Typography variant='caption' color={colors.text.caption}>
              Approver
            </Typography>
            <Typography variant='caption' color={colors.text.caption}>
              Approval Status
            </Typography>
            <Typography variant='caption' color={colors.text.caption}>
              Completion Time
            </Typography>
          </TitleWrapper>
          <LogRow log={logsByRole.op} />
          <LogRow log={logsByRole.accountant} />
          {/* Workflows that skip the Manager stage have no OP Manager logs — don't render an empty section. */}
          {!!logsByRole.opManagers?.length && (
            <>
              <Typography
                variant='caption'
                color={colors.text.caption}
                style={{
                  borderTop: `1px solid ${colors.border.default}`,
                  padding: '8px',
                }}
              >
                Manager(s)
              </Typography>
              {logsByRole.opManagers.map((log) => (
                <LogRow log={log} key={log.id} />
              ))}
            </>
          )}
          {!!logsByRole.svpManagers?.length && (
            <>
              <Typography
                variant='caption'
                color={colors.text.caption}
                style={{
                  borderTop: `1px solid ${colors.border.default}`,
                  padding: '8px',
                }}
              >
                SVP Manager(s)
              </Typography>
              {logsByRole.svpManagers.map((log) => (
                <LogRow log={log} key={log.id} />
              ))}
            </>
          )}
          {!isAssetServices &&
            timesheet.timesheetRows
            .filter((row) => row.dealOwners?.length && row.avg > 0)
            .map((row) => {
              return (
                <>
                  <Typography
                    key={row.id}
                    variant='caption'
                    color={colors.text.caption}
                    style={{
                      borderTop: `1px solid ${colors.border.default}`,
                      padding: '8px',
                    }}
                  >
                    {row.name} Deal Owner(s)
                  </Typography>
                  {row.dealOwners?.map((dealOwner) => {
                    // An owner can have several logs in the same cycle (e.g. an old APPROVED log plus
                    // a fresh PENDING one after a deal was reassigned to them). Prefer their open
                    // pending log, otherwise fall back to their latest log, so the tooltip matches
                    // the pending state shown in the cell header.
                    const ownerLogs =
                      logsByRole.dealOwners?.filter((log) => log.createdBy?.id === dealOwner.id) ?? [];
                    const ownerLog =
                      ownerLogs.find(
                        (log) => log.type === TimesheetLogType.PENDING && !log.actionEnd
                      ) ?? ownerLogs[ownerLogs.length - 1];

                    return isPendingDealOwners ? (
                      <LogRow
                        key={dealOwner.id}
                        log={ownerLog}
                        delegatedFromName={dealOwner.delegatedFromName}
                      />
                    ) : (
                      <Wrapper key={dealOwner.id}>
                        <NameWithBadge>
                          {dealOwner.delegatedFromName && (
                            <>
                              <Typography
                                variant='body'
                                color={colors.text.secondary}
                                style={{ textDecoration: 'line-through' }}
                              >
                                {dealOwner.delegatedFromName}
                              </Typography>
                              <DelegatedBadge title={`Delegated from ${dealOwner.delegatedFromName}`}>
                                <Typography variant='caption' color={colors.textAccent.inverse}>
                                  Delegated
                                </Typography>
                              </DelegatedBadge>
                            </>
                          )}
                          <Typography
                            variant='body'
                            color={
                              isPendingDealOwners ? colors.text.main : colors.textAccent.disabled
                            }
                          >
                            {dealOwner.name}
                          </Typography>
                        </NameWithBadge>
                      </Wrapper>
                    );
                  })}
                </>
              );
            })}
        </Box>
      }
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -12],
              },
            },
          ],
        },
      }}
    >
      {children}
    </StyledTooltip>
  );
};

interface TimesheetLogProps {
  log: TimesheetLog | undefined;
  /** When set, this approver is standing in for the named owner via a delegation → show a "Delegated" badge. */
  delegatedFromName?: string;
}

const LogRow = ({ log, delegatedFromName }: TimesheetLogProps) => {
  const { colors } = useTheme();

  const completionTime = useCallback((startDate: string, endDate: string) => {
    return calculateDaysBetweenDates(startDate, endDate);
  }, []);

  const statusColor = useMemo(() => {
    switch (log?.type) {
      case TimesheetLogType.PENDING:
        if (completionTime(log.actionStart, log.actionEnd) > 6) return colors.textStatus.error;
        else if (completionTime(log.actionStart, log.actionEnd) > 3)
          return colors.textStatus.warning;
        return colors.textStatus.active;
      case TimesheetLogType.REJECTED:
        return colors.textStatus.error;
      case TimesheetLogType.NOT_REQUIRED:
        return colors.textStatus.noAction;
      case TimesheetLogType.APPROVED:
      case TimesheetLogType.SUBMITTED:
        return colors.textStatus.success;
      default:
        return colors.textStatus.active;
    }
  }, [log?.type, colors.textStatus, completionTime, log?.actionStart, log?.actionEnd]);

  if (!log) return <></>;

  return (
    <Wrapper>
      <NameWithBadge>
        {delegatedFromName && (
          <>
            <Typography
              variant='body'
              color={colors.text.secondary}
              style={{ textDecoration: 'line-through' }}
            >
              {delegatedFromName}
            </Typography>
            <DelegatedBadge title={`Delegated from ${delegatedFromName}`}>
              <Typography variant='caption' color={colors.textAccent.inverse}>
                Delegated
              </Typography>
            </DelegatedBadge>
          </>
        )}
        <Typography variant='body' color={colors.text.main}>
          {log.createdBy?.name}
        </Typography>
      </NameWithBadge>
      <Chip
        label={
          <Typography variant='body' color={statusColor}>
            {log.type}
          </Typography>
        }
        style={{
          backgroundColor: colors.surfaceBackground.highlighted,
          width: 'min-content',
        }}
      />
      <LabelWrapper>
        <Typography variant='body' color={colors.text.secondary}>
          {`${completionTime(log.actionStart, log.actionEnd)} ${
            completionTime(log.actionStart, log.actionEnd) > 1 ? 'days' : 'day'
          }`}
        </Typography>
        <Typography variant='body' color={colors.text.secondary}>
          {log.actionEnd ? `(${format(parseISO(log.actionEnd), 'MMM d, yyyy')})` : '-'}
        </Typography>
      </LabelWrapper>
    </Wrapper>
  );
};

const TitleWrapper = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  padding: 0 8px 8px;
`;

const Wrapper = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  padding: 4px 8px;
`;

const LabelWrapper = styled('div')`
  display: grid;
  grid-template-columns: 1fr 2fr;
`;

const NameWithBadge = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
`;

const DelegatedBadge = styled('div')`
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.surfaceAccent.default};
  white-space: nowrap;
`;

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.colors.surfaceStatus.active,
    color: theme.colors.text.main,
    boxShadow: theme.shadows[1],
    fontSize: 11,
    minWidth: '583px',
    maxHeight: '362px',
    overflowY: 'scroll',
    borderRadius: '4px',
  },
}));
